import React, { useState } from 'react'
import { FormikValues } from 'formik'
import { datadogRum } from '@datadog/browser-rum'

import {
  Flex,
  GlobalStyles,
  Grid,
  Notification,
} from '@mindfulchefuk/design-system'

import { GiftCardFormState } from '@mindfulchefuk/features/GiftCards/interfaces'
import routesConfig from '@mindfulchefuk/config/routesConfig'
import { postGiftCards } from '@mindfulchefuk/services/postGiftCard'
import { paymentsAnonymousTransaction } from '@mindfulchefuk/services/paymentsAnonymousTransaction/paymentsAnonymousTransaction'
import { paymentsTransaction } from '@mindfulchefuk/services/paymentsTransaction/paymentsTransaction'
import { getCustomerId } from '@mindfulchefuk/helpers/js/authentication'
import { useGetCardDetails } from '@mindfulchefuk/hooks/useGetCardDetails'
import useScrollToTop from '@mindfulchefuk/hooks/useScrollToTop'
import { NumberBasedStepper } from '@mindfulchefuk/design-system/NavStepper/NumberBasedStepper'
import { RecipientFormContainer } from '@mindfulchefuk/features/GiftCards/components/RecipientFormContainer'
import { PaymentForm } from '@mindfulchefuk/features/GiftCards/components/PaymentForm'
import { Confirmation } from '@mindfulchefuk/features/GiftCards/components/Confirmation'

import {
  GIFTCARDS_STEPS,
  PAYMENT_GIFT_CARD_ORDER_PREPEND,
} from '@mindfulchefuk/constants'
import { NextPageWithLayout } from '@mindfulchefuk/types/NextJS'
import { useGiftCardState } from '@mindfulchefuk/features/GiftCards/hooks/useGiftCardState'

import {
  trackErrorWithMakePayment,
  trackErrorWithPaymentComplete,
  trackPaymentComplete,
  trackMakePayment,
} from '@mindfulchefuk/forms/CardField/utils/tracking'
import { ErrorMessage } from '@mindfulchefuk/features/Notifications/interfaces'
import { FAQGiftCardCopy } from '@mindfulchefuk/features/GiftCards/constants/FAQ.copy'
import { FAQ } from '@mindfulchefuk/components/FAQ/FAQ'
import MainLayout from '@mindfulchefuk/layouts/MainLayout'
import { useGetCustomer } from '@mindfulchefuk/features/Customer'
import { emptyCustomerData } from '@mindfulchefuk/features/Customer/utils/emptyCustomerData'
import { generateCheckoutErrorConfig } from '@mindfulchefuk/features/Notifications/errors/checkoutError'
import { useGiftCardPaymentPolling } from '@mindfulchefuk/features/GiftCards/hooks/useGiftCardPaymentPolling'

export const GiftCards: NextPageWithLayout = () => {
  const customerId = getCustomerId()
  const isLoggedInUser = !!customerId

  const { data } = useGetCustomer()
  const customer = data || emptyCustomerData
  const { firstName, lastName, email } = customer

  const [{ currentStep, formState }, handlers] = useGiftCardState()

  const { data: cardDetails } = useGetCardDetails(customerId, {
    enabled: isLoggedInUser,
  })

  const [redirectUrl, setRedirectUrl] = useState<string | null>(undefined)
  const [disableStepper, setDisableStepper] = useState(false)
  const [giftCardId, setGiftcardId] = useState(null)
  const [checkoutError, setCheckoutError] = useState<ErrorMessage | null>(null)

  useScrollToTop([currentStep, checkoutError])

  const handlePaymentSuccess = () => {
    handlers.setCurrentStep(2)
    trackPaymentComplete()
  }

  const handlePaymentFailure = (error: Error) => {
    trackErrorWithPaymentComplete(error)
    datadogRum.addAction('Gift-card-polling', {
      error,
      type: 'generic-error',
    })
    setCheckoutError(generateCheckoutErrorConfig(error))
  }

  const { startPolling } = useGiftCardPaymentPolling({
    giftCardId,
    onSuccess: handlePaymentSuccess,
    onFailure: ({ error }) => handlePaymentFailure(error),
  })

  const handleAnonymousPayment = async (newFields: GiftCardFormState) => {
    const {
      data: { id },
    } = await postGiftCards({
      ...formState,
      ...newFields,
      customerId,
      isLoggedInUser,
      triggerPayment: false,
    })

    setGiftcardId(id)

    const { url } = await paymentsAnonymousTransaction({
      cardToken: newFields.paymentToken,
      purchaseId: `${PAYMENT_GIFT_CARD_ORDER_PREPEND}${id}`,
    })

    setRedirectUrl(url)
  }

  const handleLoggedInPayment = async (newFields: GiftCardFormState) => {
    const {
      data: { id },
    } = await postGiftCards({
      ...formState,
      ...newFields,
      customerId,
      isLoggedInUser,
      triggerPayment: false,
    })

    setGiftcardId(id)

    const { url } = await paymentsTransaction({
      purchaseId: `${PAYMENT_GIFT_CARD_ORDER_PREPEND}${id}`,
    })

    if (url) {
      setRedirectUrl(url)
    }

    if (id) {
      startPolling({ id })
    }
  }

  const handleSubmit = async (
    newFields: GiftCardFormState,
    { setSubmitting }: FormikValues
  ) => {
    setCheckoutError(null)
    setDisableStepper(true)
    handlers.setFormState({ ...formState, ...newFields })
    trackMakePayment()
    try {
      if (isLoggedInUser) {
        await handleLoggedInPayment(newFields)
      } else {
        await handleAnonymousPayment(newFields)
      }

      setSubmitting(false)
    } catch (errors) {
      trackErrorWithMakePayment(errors)
      datadogRum.addAction('submit-gift-card-order', {
        ...errors,
        type: 'generic-error',
      })
      setCheckoutError(generateCheckoutErrorConfig(errors))
      setSubmitting(false)
      setDisableStepper(false)
    }
  }

  return (
    <>
      <GlobalStyles />
      <Grid justifyContent="center" py={{ base: 40, md: 56 }}>
        <Flex
          direction="column"
          maxWidth="592px"
          pb={56}
          justifyContent="center"
          mx={{ base: 20, md: 0 }}
        >
          <NumberBasedStepper
            currentStep={currentStep}
            setCurrentStep={handlers.setCurrentStep}
            disabled={
              disableStepper || currentStep === GIFTCARDS_STEPS.CONFIRMATION
            }
            steps={[
              {
                step: GIFTCARDS_STEPS.RECIPIENT_DETAILS,
                label: routesConfig.giftCardsYourGiftCard.label,
              },
              {
                step: GIFTCARDS_STEPS.PAYMENT_DETAILS,
                label: routesConfig.giftCardsPayment.label,
              },
              {
                step: GIFTCARDS_STEPS.CONFIRMATION,
                label: routesConfig.giftCardsConfirmation.label,
              },
            ]}
            testId="checkout-navigation-list"
            forceLastStepToBeEnabledIfActive
          />

          {checkoutError && (
            <Notification
              type="inline"
              variant="error"
              title={checkoutError.title}
              mb={16}
            >
              {checkoutError.text}
            </Notification>
          )}

          {currentStep === GIFTCARDS_STEPS.RECIPIENT_DETAILS ? (
            <RecipientFormContainer
              amount={formState.amount}
              recipientFirstName={formState.recipientFirstName}
              recipientLastName={formState.recipientLastName}
              recipientEmail={formState.recipientEmail}
              recipientEmailConfirm={formState.recipientEmailConfirm}
              personalMessage={formState.personalMessage}
              onSubmit={(newFields) => {
                handlers.setFormState(newFields)
                handlers.setCurrentStep((step: number) => step + 1)
              }}
              sendGiftCardToRecipient={formState.sendGiftCardToRecipient}
            />
          ) : null}
          {currentStep === GIFTCARDS_STEPS.PAYMENT_DETAILS ? (
            <PaymentForm
              firstName={firstName}
              lastName={lastName}
              email={email}
              amount={formState.amount}
              redirectUrl={redirectUrl}
              onSubmit={handleSubmit}
              cardDetails={cardDetails}
              giftCardId={giftCardId}
              isLoggedInUser={isLoggedInUser}
              onSuccess={handlePaymentSuccess}
              onFailure={handlePaymentFailure}
              on3dsError={() => setRedirectUrl(undefined)}
            />
          ) : null}
          {currentStep === GIFTCARDS_STEPS.CONFIRMATION ? (
            <Confirmation
              email={formState.email || email}
              recipientFirstName={formState.recipientFirstName}
              recipientLastName={formState.recipientLastName}
              recipientEmail={formState.recipientEmail}
              isLoggedInUser={isLoggedInUser}
              restartJourney={() => {
                setDisableStepper(false)
                setRedirectUrl(undefined)
                handlers.restartJourney()
              }}
              sendGiftCardToRecipient={formState.sendGiftCardToRecipient}
            />
          ) : null}
        </Flex>
      </Grid>
      <FAQ questions={FAQGiftCardCopy} />
    </>
  )
}

GiftCards.getLayout = (page) => (
  <MainLayout hideFooter title={routesConfig.giftCard.label}>
    {page}
  </MainLayout>
)

export default GiftCards

export { getStaticGlobalContent as getStaticProps } from '@mindfulchefuk/features/CMS/services/getStaticData'
